import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Send, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getPreviewVariables } from '@/lib/emailVariables';

interface EmailTemplate {
  id: string;
  template_name: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables: string[];
  is_active: boolean;
  category: string;
}

interface EmailTemplateTestDialogProps {
  template: EmailTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TEST_CLIENT_ID = '00000000-0000-0000-0000-000000000000';

export default function EmailTemplateTestDialog({ template, open, onOpenChange }: EmailTemplateTestDialogProps) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [error, setError] = useState('');

  const sendTestMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          template_id: template.id,
          client_id: TEST_CLIENT_ID,
          recipient_email: email,
          variables: getPreviewVariables(),
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Test email sent',
        description: `Successfully sent test email to ${recipientEmail}`,
      });
      onOpenChange(false);
      setRecipientEmail('');
      setError('');
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to send test email');
    },
  });

  const handleSend = () => {
    setError('');
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!recipientEmail || !emailRegex.test(recipientEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    sendTestMutation.mutate(recipientEmail);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Send Test Email</DialogTitle>
          <DialogDescription>
            Send a test version of this template with sample data
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Template</Label>
            <p className="text-sm text-muted-foreground">{template.template_name}</p>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Subject</Label>
            <p className="text-sm text-muted-foreground">{template.subject}</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="recipient-email">Recipient Email *</Label>
            <Input
              id="recipient-email"
              type="email"
              placeholder="Enter test recipient email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              disabled={sendTestMutation.isPending}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sendTestMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sendTestMutation.isPending}
          >
            {sendTestMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Test
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
